import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { Button } from '@libero/core/components/ui/button';
import { attributText, zahl } from '../formate';
import { hinweiseAusHeader, weitereAusHeader } from '../hinweise';
import { dateinameAusHeader } from '../download';
import { empfaengerAnzeige, wegText } from '../lauf';
import type { LieferantenBlock, VersandInfo } from '../typen';

async function fehlerText(err: any): Promise<string> {
  try {
    const daten = err?.response?.data;
    if (daten && typeof daten.text === 'function') {
      const msg = JSON.parse(await daten.text())?.error?.message;
      if (typeof msg === 'string' && msg !== '') return msg;
    }
  } catch {
    // Antwort war kein JSON — Standardtext
  }
  return 'Datei konnte nicht erzeugt werden';
}

export default function LieferantenTabelle({
  block,
  laufId,
  versand,
}: {
  block: LieferantenBlock;
  laufId: string;
  versand?: VersandInfo;
}) {
  const [laedt, setLaedt] = useState(false);
  const [hinweise, setHinweise] = useState<string[]>([]);
  const [weitereHinweise, setWeitereHinweise] = useState(0);
  const [fehler, setFehler] = useState<string | null>(null);
  /**
   * Zuletzt geladene Datei. Dauert die Anfrage länger (Vorschau wird neu aus Plenty gelesen), wertet Chrome
   * den Download nicht mehr als Klick und verwirft ihn still (gemessen 04.11.2026). Der Speichern-Link ist dann
   * ein echter Klick. URL erst beim nächsten Laden bzw. beim Verlassen widerrufen.
   */
  const [datei, setDatei] = useState<{ url: string; name: string } | null>(null);
  useEffect(() => () => {
    if (datei) URL.revokeObjectURL(datei.url);
  }, [datei]);

  const herunterladen = async () => {
    setLaedt(true);
    setFehler(null);
    setDatei(null);
    try {
      const res = await api.post(
        '/programmes/daily-order/datei',
        { laufId, lieferantId: block.id },
        { responseType: 'blob' },
      );
      const name = dateinameAusHeader(String(res.headers?.['content-disposition'] ?? ''), `${block.name}-Bestellungen`);
      setHinweise(hinweiseAusHeader(res.headers?.['x-daily-order-hinweise']));
      setWeitereHinweise(weitereAusHeader(res.headers?.['x-daily-order-hinweise-weitere']));
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setDatei({ url, name });
    } catch (err: any) {
      setFehler(await fehlerText(err));
    } finally {
      setLaedt(false);
    }
  };

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-semibold text-[#1A1A1A]">
          {block.name}{' '}
          <span className="text-sm font-normal text-gray-600">
            {zahl(block.positionen.length)} Positionen · {zahl(block.auftragsIds.length)} Aufträge
          </span>
        </h2>
        {block.positionen.length > 0 && (
          <Button
            onClick={herunterladen}
            disabled={laedt}
            className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
          >
            <Download className="h-4 w-4 mr-2" />
            {laedt ? 'Lädt...' : 'Datei herunterladen'}
          </Button>
        )}
      </div>
      {fehler && <p className="p-3 rounded text-sm bg-red-50 text-red-800">{fehler}</p>}
      {datei && (
        <p className="text-sm text-gray-700">
          Datei fertig: {datei.name}. Kein Download gestartet?{' '}
          <a href={datei.url} download={datei.name} className="font-semibold text-[#1A1A1A] underline">
            Hier speichern
          </a>
        </p>
      )}
      {hinweise.length > 0 && (
        <div className="p-3 rounded text-sm bg-yellow-50 text-yellow-900">
          <p className="font-semibold">Hinweise zur Datei</p>
          <ul className="list-disc pl-5">
            {hinweise.map((h, i) => (
              <li key={i}>{h}</li>
            ))}
          </ul>
          {weitereHinweise > 0 && <p className="mt-1">+{weitereHinweise} weitere (siehe Server-Log)</p>}
        </div>
      )}
      <p className="text-sm text-gray-700">
        Empfänger: {empfaengerAnzeige(block.empfaenger, versand)} · Weg: {wegText(block.weg)}
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border border-[#DAD9D9]">
          <thead className="bg-[#DAD9D9] text-[#1A1A1A]">
            <tr>
              <th className="px-3 py-2 text-left">Auftrag</th>
              <th className="px-3 py-2 text-right">Menge</th>
              <th className="px-3 py-2 text-left">EAN</th>
              <th className="px-3 py-2 text-left">Artikelnummer</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Attribute</th>
              <th className="px-3 py-2 text-right">Lager</th>
              <th className="px-3 py-2 text-left">Externe ID</th>
              <th className="px-3 py-2 text-left">Quelle</th>
            </tr>
          </thead>
          <tbody>
            {block.positionen.map((p, i) => (
              <tr key={`${p.orderId}-${p.variationId}-${i}`} className="border-t border-[#DAD9D9]">
                <td className="px-3 py-2">{p.orderId}</td>
                <td className="px-3 py-2 text-right">
                  {zahl(p.menge)}
                  {p.istSet && (
                    <span
                      className="ml-2 px-2 py-0.5 rounded bg-[#E9FF57] text-[#1A1A1A] text-xs"
                      title={`bestellt: ${p.menge} (Auftrag: ${p.mengeOriginal} Sets)`}
                    >
                      Set × 10
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {p.ean || '—'}
                  {p.eanMehrdeutig && (
                    <span
                      className="ml-2 px-2 py-0.5 rounded bg-yellow-100 text-yellow-900 text-xs"
                      title={p.alleEans.join(', ')}
                    >
                      mehrere EANs
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">{p.nummer}</td>
                <td className="px-3 py-2">{p.name}</td>
                <td className="px-3 py-2">{attributText(p.attribute)}</td>
                <td className="px-3 py-2 text-right">{p.warehouseId}</td>
                <td className="px-3 py-2">{p.externeId || '—'}</td>
                <td className="px-3 py-2">
                  {p.quelle === 'live' && (
                    <span className="px-2 py-0.5 rounded bg-[#DAD9D9] text-[#1A1A1A] text-xs">live</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
